import React, { useMemo, useState } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import "../shared/styles/auth.css"
import OdontoFlowLogo from "../shared/brand/OdontoFlowLogo"

const initialForm = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  clinicName: "",
  acceptTerms: false,
}

function normalizeError(err) {
  const message =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "Error al crear cuenta"

  return Array.isArray(message) ? message[0] : message
}

export default function RegisterPage() {
  const navigate = useNavigate()

  const [form, setForm] = useState(initialForm)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [createdAccount, setCreatedAccount] = useState(null)
  const [resending, setResending] = useState(false)
  const [resendMessage, setResendMessage] = useState("")

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.email.trim() &&
      form.clinicName.trim() &&
      form.password &&
      form.confirmPassword &&
      form.acceptTerms
    )
  }, [form])

  function handleChange(e) {
    const { name, value, type, checked } = e.target

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  function validateForm() {
    const email = form.email.trim().toLowerCase()

    if (!form.name.trim()) {
      return "Escribe tu nombre completo"
    }

    if (!form.clinicName.trim()) {
      return "Escribe el nombre de tu clínica o consultorio"
    }

    if (!email.includes("@")) {
      return "Escribe un correo válido"
    }

    if (form.password.length < 8) {
      return "La contraseña debe tener al menos 8 caracteres"
    }

    if (form.password !== form.confirmPassword) {
      return "Las contraseñas no coinciden"
    }

    if (!form.acceptTerms) {
      return "Debes aceptar los términos y el aviso de privacidad"
    }

    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      clinicName: form.clinicName.trim(),
    }

    try {
      setLoading(true)
      setError(null)
      setResendMessage("")

      const { data } = await axios.post("/api/onboarding/register-founder", payload)

      setCreatedAccount({
        email: payload.email,
        clinicName: payload.clinicName,
        debugVerificationUrl: data?.debugVerificationUrl || "",
        emailDeliverySent: Boolean(data?.emailDeliverySent),
        emailDeliveryMode: data?.emailDeliveryMode || "",
      })
    } catch (err) {
      setError(normalizeError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleResendVerification() {
    const email = createdAccount?.email || form.email.trim().toLowerCase()
    if (!email) return

    try {
      setResending(true)
      setResendMessage("")
      setError(null)

      const { data } = await axios.post("/api/auth/resend-verification", { email })

      setResendMessage(data?.message || "Te enviamos un nuevo correo de confirmación.")
      setCreatedAccount((prev) => ({
        ...(prev || { email }),
        debugVerificationUrl: data?.debugVerificationUrl || prev?.debugVerificationUrl || "",
        emailDeliverySent: Boolean(data?.emailDeliverySent),
        emailDeliveryMode: data?.emailDeliveryMode || prev?.emailDeliveryMode || "",
      }))
    } catch (err) {
      setError(normalizeError(err))
    } finally {
      setResending(false)
    }
  }

  if (createdAccount) {
    return (
      <div className="of-auth-page">
        <div className="of-auth-bg-glow of-auth-bg-glow-1" />
        <div className="of-auth-bg-glow of-auth-bg-glow-2" />

        <div className="of-auth-shell of-auth-shell--register">
          <section className="of-auth-brand of-auth-brand--compact">
            <div className="of-auth-brand-top">
              <div className="of-auth-badge">Cuenta creada</div>
              <div className="of-auth-logo-row">
                <OdontoFlowLogo
                  theme="dark"
                  size="lg"
                  showDescriptor
                  descriptor="Tu clínica quedó lista. Solo falta confirmar tu correo."
                  style={{ maxWidth: 520, width: "100%" }}
                />
              </div>
            </div>
          </section>

          <section className="of-auth-panel">
            <div className="of-auth-form-wrap">
              <div className="of-auth-form-card">
                <div className="of-auth-form-header">
                  <div className="of-auth-form-kicker">Activación pendiente</div>
                  <h2>Confirma tu correo</h2>
                  <p>
                    Enviamos una liga de activación a <strong>{createdAccount.email}</strong>.
                    Abre ese correo para activar tu cuenta y después inicia sesión.
                  </p>
                </div>

                {error ? <div className="of-auth-error">{error}</div> : null}

                {resendMessage ? (
                  <div className="of-auth-helper" style={{ color: "#166534" }}>
                    {resendMessage}
                  </div>
                ) : null}

                {createdAccount.debugVerificationUrl ? (
                  <div className="of-auth-error" style={{ background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}>
                    Modo local/debug: <br />
                    <a href={createdAccount.debugVerificationUrl} style={{ color: "#1d4ed8", fontWeight: 900, wordBreak: "break-all" }}>
                      Abrir liga de verificación
                    </a>
                  </div>
                ) : null}

                <div className="of-auth-secondary-actions">
                  <button
                    type="button"
                    className="of-auth-submit"
                    onClick={() => navigate("/login")}
                  >
                    Ir a iniciar sesión
                  </button>

                  <button
                    type="button"
                    className="of-auth-muted-button"
                    onClick={handleResendVerification}
                    disabled={resending}
                  >
                    {resending ? "Reenviando..." : "Reenviar correo"}
                  </button>
                </div>

                <div className="of-auth-helper">
                  Si no lo ves, revisa spam o promociones.
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    )
  }

  return (
    <div className="of-auth-page">
      <div className="of-auth-bg-glow of-auth-bg-glow-1" />
      <div className="of-auth-bg-glow of-auth-bg-glow-2" />

      <div className="of-auth-shell of-auth-shell--register">
        <section className="of-auth-brand of-auth-brand--compact">
          <div className="of-auth-brand-top">
            <div className="of-auth-badge">Comienza tu prueba</div>

            <div className="of-auth-logo-row">
              <OdontoFlowLogo
                theme="dark"
                size="lg"
                showDescriptor
                descriptor="Registra tu clínica y comienza a operar."
                style={{
                  maxWidth: 520,
                  width: "100%",
                }}
              />
            </div>
          </div>

          <div className="of-auth-divider" />

          <div className="of-auth-feature-list">
            <div className="of-auth-feature">
              <span className="of-auth-feature-icon blue" />
              <div>
                <strong>14 días de prueba</strong>
                <p>Arranca con agenda, pacientes, tratamientos y cobranza.</p>
              </div>
            </div>

            <div className="of-auth-feature">
              <span className="of-auth-feature-icon purple" />
              <div>
                <strong>Cuenta fundadora</strong>
                <p>Se crea tu clínica, usuario administrador y primer sillón.</p>
              </div>
            </div>

            <div className="of-auth-feature">
              <span className="of-auth-feature-icon green" />
              <div>
                <strong>Activa tu correo</strong>
                <p>Después del registro confirma tu email para entrar a tu agenda.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="of-auth-panel">
          <div className="of-auth-form-wrap">
            <div className="of-auth-form-card">
              <div className="of-auth-form-header">
                <div className="of-auth-form-kicker">Registro seguro</div>
                <h2>Crear cuenta</h2>
                <p>Registra tu clínica para comenzar a operar OdontoFlow</p>
              </div>

              {error ? <div className="of-auth-error">{error}</div> : null}

              <form onSubmit={handleSubmit} className="of-auth-form">
                <div className="of-auth-field">
                  <label>Nombre completo</label>
                  <input
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Ej. Oscar Barron"
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="of-auth-field">
                  <label>Nombre de clínica o consultorio</label>
                  <input
                    name="clinicName"
                    type="text"
                    autoComplete="organization"
                    placeholder="Ej. Consultorio Dental Sonrisa"
                    value={form.clinicName}
                    onChange={handleChange}
                  />
                </div>

                <div className="of-auth-field">
                  <label>Correo</label>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="tu@clinica.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="of-auth-grid-2">
                  <div className="of-auth-field">
                    <label>Contraseña</label>
                    <input
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                      value={form.password}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="of-auth-field">
                    <label>Confirmar contraseña</label>
                    <input
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Repite tu contraseña"
                      value={form.confirmPassword}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <label className="of-auth-check">
                  <input
                    name="acceptTerms"
                    type="checkbox"
                    checked={form.acceptTerms}
                    onChange={handleChange}
                  />
                  <span>
                    Acepto términos de uso y aviso de privacidad de OdontoFlow.
                  </span>
                </label>

                <button
                  className="of-auth-submit"
                  type="submit"
                  disabled={loading || !canSubmit}
                >
                  {loading ? "Creando cuenta..." : "Crear cuenta"}
                </button>
              </form>

              <div className="of-auth-secondary-actions">
                <button
                  type="button"
                  className="of-auth-link-button"
                  onClick={() => navigate("/login")}
                >
                  Ya tengo cuenta
                </button>
              </div>

              <div className="of-auth-helper">
                Registro seguro · OdontoFlow
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
